/**
 * AllocationFilter plugin.
 *
 * Filters grant calls by total allocation amount range (min/max in PLN).
 * URL/API/SavedView param names: allocationMin, allocationMax (same across all).
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useCallback } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";

/**
 * Value type for the allocation filter.
 */
export interface AllocationValue {
  min: number | undefined;
  max: number | undefined;
}

/**
 * Allocation filter React component.
 * Renders two number inputs (min/max) for allocation range in PLN.
 */
const AllocationFilterComponent = React.memo(
  function AllocationFilterComponent(
    props: FilterComponentProps<AllocationValue>
  ) {
    const { value, onChange } = props;

    const handleMinChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...value,
          min: e.target.value ? parseFloat(e.target.value) : undefined,
        });
      },
      [value, onChange]
    );

    const handleMaxChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...value,
          max: e.target.value ? parseFloat(e.target.value) : undefined,
        });
      },
      [value, onChange]
    );

    return React.createElement("div", null,
      React.createElement("label", {
        className: "block text-sm font-medium text-neutral-700 mb-2",
      }, "Alokacja (w PLN)"),
      React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        React.createElement("div", null,
          React.createElement("label", {
            htmlFor: "allocationMin",
            className: "block text-xs text-neutral-600 mb-1",
          }, "Minimum"),
          React.createElement("input", {
            type: "number",
            id: "allocationMin",
            min: "0",
            step: "1000",
            value: value.min ?? "",
            onChange: handleMinChange,
            placeholder: "np. 1000000",
            className: "w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none",
          })
        ),
        React.createElement("div", null,
          React.createElement("label", {
            htmlFor: "allocationMax",
            className: "block text-xs text-neutral-600 mb-1",
          }, "Maximum"),
          React.createElement("input", {
            type: "number",
            id: "allocationMax",
            min: "0",
            step: "1000",
            value: value.max ?? "",
            onChange: handleMaxChange,
            placeholder: "np. 10000000",
            className: "w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none",
          })
        )
      ),
      React.createElement("p", {
        className: "mt-1 text-xs text-neutral-500",
      }, "Filtruj nabory według całkowitej kwoty alokacji")
    );
  }
);

export const AllocationFilterPlugin: FilterPlugin<AllocationValue> = {
  key: "allocation",
  label: "Alokacja",
  placement: "advanced",
  order: 5,
  defaultValue: { min: undefined, max: undefined },
  Component: AllocationFilterComponent,

  isActive: (value: AllocationValue): boolean => {
    return value.min !== undefined || value.max !== undefined;
  },

  toUrlParams: (value: AllocationValue, params: URLSearchParams): void => {
    if (value.min !== undefined) {
      params.set("allocationMin", value.min.toString());
    }
    if (value.max !== undefined) {
      params.set("allocationMax", value.max.toString());
    }
  },

  fromUrlParams: (params: URLSearchParams): AllocationValue => {
    const minStr = params.get("allocationMin");
    const maxStr = params.get("allocationMax");
    const min = minStr ? parseFloat(minStr) : undefined;
    const max = maxStr ? parseFloat(maxStr) : undefined;
    return {
      min: min !== undefined && !isNaN(min) ? min : undefined,
      max: max !== undefined && !isNaN(max) ? max : undefined,
    };
  },

  toSavedView: (
    value: AllocationValue,
    filters: Partial<SavedViewFilters>
  ): void => {
    if (value.min !== undefined) {
      filters.allocationMin = value.min;
    }
    if (value.max !== undefined) {
      filters.allocationMax = value.max;
    }
  },

  fromSavedView: (filters: SavedViewFilters): AllocationValue => {
    return {
      min: filters.allocationMin,
      max: filters.allocationMax,
    };
  },

  toApiParams: (value: AllocationValue, params: URLSearchParams): void => {
    if (value.min !== undefined) {
      params.set("allocationMin", value.min.toString());
    }
    if (value.max !== undefined) {
      params.set("allocationMax", value.max.toString());
    }
  },
};
